import { Matrix } from 'app/models/questions/Matrix'
import { QuestionComponent } from 'app/models/questions/QuestionComponent'
import { ImageComponent } from 'app/modules/survey/survey-viewer/survey-question/types/image/image.component'
import { ImagePropertyValidator } from 'app/models/questions/property-validator/ImagePropertyValidator'
import { IImage } from 'app/interfaces/IImage'
import { IImageSelection } from 'app/interfaces/IImageSelection'
import { requireMinImageSelection } from 'app/form-validators/RequireMinImageSelectionValidator'
import { AbstractControl, UntypedFormGroup } from '@angular/forms'

export class Image extends Matrix implements IImage {
  private readonly _imageSelection: IImageSelection

  constructor(question) {
    super(question)

    this._imageSelection = question.imageSelection

    new ImagePropertyValidator(question).validate()
  }

  getDisplayComponent() {
    return new QuestionComponent(ImageComponent, this)
  }

  applyMaxAnswers(surveyForm) {
    return this.limitAnswersAmount
  }

  isOpenStatement(rowKey, statementKey) {
    if (this.rows[rowKey].isOpen) {
      return true
    }

    return this.rows[rowKey].openCols[statementKey]
  }

  getOpenStatementsForRow(rowKey: string): string[] {
    return this.rows[rowKey].isOpen ? this.statementKeys : []
  }

  buildFormGroupValidators() {
    return this.isOptional ? [] : [requireMinImageSelection(this)]
  }

  selectImage(statementKey: string, questionForm: AbstractControl): boolean {
    // Utiliser rowKeys (toujours synchronisé avec rows) et filtrer pour ne garder que les rows disponibles dans le formulaire
    // Préserver l'ordre en utilisant rowsOrder filtré
    const rowsInRowKeys = this.rowsOrder.filter(rKey => this.rowKeys.includes(rKey));
    
    const orderedAvailableRows = rowsInRowKeys.filter(rKey => {
      const rowControl = questionForm.get(rKey);
      return rowControl && !rowControl.disabled;
    });

    if (orderedAvailableRows.length === 0) {
      return false;
    }

    let previouslySelectedImageCount = 0
    let rowKeyOfNewlySelectedImage = null
    const selectedStatements = {}
    orderedAvailableRows.forEach(rKey => {
      const rowControl = questionForm.get(rKey);
      if (!rowControl) return;
      
      const formControlNameControl = rowControl.get(this.formControlName);
      if (!formControlNameControl) return;
      
      const aRow = formControlNameControl.value;
      selectedStatements[rKey] = Object.entries(aRow).find(a => a[1])?.[0];
      if (aRow[statementKey]) {
        rowKeyOfNewlySelectedImage = rKey
        delete selectedStatements[rKey]
      }
    })
    previouslySelectedImageCount = Object.values(selectedStatements).filter(Boolean).length

    if (previouslySelectedImageCount === orderedAvailableRows.length && !rowKeyOfNewlySelectedImage) {
      return false
    }

    if (rowKeyOfNewlySelectedImage) {
      // Faire toutes les modifications sans émettre d'événements
      this.resetQuestionForm(questionForm, false)

      // Séparer les sélections en deux groupes : verrouillées et non verrouillées
      // Les images verrouillées doivent aussi être réorganisées pour maintenir l'ordre séquentiel
      const allSelections: {rowKey: string, statementKey: string, isLocked: boolean}[] = [];
      
      orderedAvailableRows.forEach(rKey => {
        const statementKey = selectedStatements[rKey];
        if (statementKey) {
          const isLocked = this.statements[statementKey]?.positionLocked === true;
          allSelections.push({rowKey: rKey, statementKey: statementKey, isLocked: isLocked});
        }
      });

      // Trier les sélections pour préserver l'ordre relatif :
      // - Les images verrouillées gardent leur ordre relatif entre elles
      // - Les images non verrouillées gardent leur ordre relatif entre elles
      // - L'ordre global est maintenu (verrouillées et non verrouillées mélangées selon leur position d'origine)
      // On crée un map pour connaître l'index original de chaque sélection
      const originalIndexMap = new Map<string, number>();
      orderedAvailableRows.forEach((rKey, index) => {
        const statementKey = selectedStatements[rKey];
        if (statementKey) {
          originalIndexMap.set(statementKey, index);
        }
      });

      // Trier toutes les sélections par leur index original pour maintenir l'ordre
      allSelections.sort((a, b) => {
        const indexA = originalIndexMap.get(a.statementKey) ?? 0;
        const indexB = originalIndexMap.get(b.statementKey) ?? 0;
        return indexA - indexB;
      });

      // Réassigner toutes les images dans l'ordre séquentiel (sans émettre d'événements)
      allSelections.forEach((selection, index) => {
        const targetRowKey = orderedAvailableRows[index];
        if (!targetRowKey) return;
        
        const rowControl = questionForm.get(targetRowKey);
        if (!rowControl) return;
        
        const formControlNameControl = rowControl.get(this.formControlName);
        if (!formControlNameControl) return;
        
        const statementControl = formControlNameControl.get(selection.statementKey);
        if (statementControl) {
          statementControl.setValue(true, {emitEvent: false});
        }
      });

      // Déclencher un seul événement à la fin pour synchroniser avec Firebase
      // On marque le formulaire comme modifié et on déclenche valueChanges une seule fois
      // Cela garantit que toutes les modifications sont faites avant la synchronisation avec Firebase
      if (questionForm instanceof UntypedFormGroup) {
        questionForm.markAsDirty();
        // Déclencher valueChanges en modifiant une valeur existante pour forcer l'événement
        // On prend la première row disponible et on modifie une valeur pour déclencher valueChanges
        const firstRowKey = orderedAvailableRows[0];
        if (firstRowKey) {
          const rowControl = questionForm.get(firstRowKey);
          if (rowControl) {
            const formControlNameControl = rowControl.get(this.formControlName);
            if (formControlNameControl) {
              // Modifier une valeur existante pour déclencher valueChanges
              // On récupère la valeur actuelle, on la modifie temporairement, puis on la remet
              // Cela garantit que valueChanges est déclenché même si les valeurs n'ont pas changé
              const currentValue = formControlNameControl.value;
              const firstStatementKey = this.statementKeys[0];
              if (firstStatementKey) {
                const statementControl = formControlNameControl.get(firstStatementKey);
                if (statementControl) {
                  const currentStatementValue = statementControl.value;
                  // Modifier temporairement la valeur pour déclencher l'événement
                  statementControl.setValue(!currentStatementValue, {emitEvent: false});
                  // Remettre la valeur originale et déclencher l'événement
                  statementControl.setValue(currentStatementValue, {emitEvent: true});
                }
              }
            }
          }
        }
      }

    } else {
      const targetRowKey = orderedAvailableRows[previouslySelectedImageCount];
      const rowControl = questionForm.get(targetRowKey);
      if (!rowControl) return false;
      
      const formControlNameControl = rowControl.get(this.formControlName);
      if (!formControlNameControl) return false;
      
      const statementControl = formControlNameControl.get(statementKey);
      if (!statementControl) return false;
      
      statementControl.setValue(true, {emitEvent: true});
    }

    return true
  }

  addAvailableControlsForRow(statementKeys: string[], surveyForm: UntypedFormGroup, rKey: string, availableControls: {}) {
    const questionControl = surveyForm.controls[this.key];
    if (!questionControl) return;
    
    const rowControl = questionControl.get(rKey);
    if (!rowControl || rowControl.disabled) return;
    
    const formControlNameControl = rowControl.get(this.formControlName);
    if (!formControlNameControl) return;
    
    statementKeys.forEach(sKey => {
      const statementControl = formControlNameControl.get(sKey);
      if (!statementControl) return;
      
      statementControl.updateValueAndValidity()
      if (
        statementControl.enabled &&
        !availableControls[sKey] &&
        !this.findRowKeyOfSelectedImage(sKey, questionControl)
      ) {
        availableControls[sKey] = true
      }
    })
  }

  findRowKeyOfSelectedImage(statementKey: string, formControl: AbstractControl): string {
    let rowKey = ''

    // Utiliser rowsOrder pour respecter l'ordre et filtrer pour ne garder que les rows disponibles
    const rowsInRowKeys = this.rowsOrder.filter(rKey => this.rowKeys.includes(rKey));
    
    for (const rKey of rowsInRowKeys) {
      const rowControl = formControl.get(rKey);
      if (!rowControl) continue;
      
      const formControlNameControl = rowControl.get(this.formControlName);
      if (!formControlNameControl) continue;
      
      const statementControl = formControlNameControl.get(statementKey);
      if (statementControl && statementControl.value) {
        rowKey = rKey
        break
      }
    }

    return rowKey
  }

  private resetQuestionForm(questionForm: AbstractControl, emitEvent = true) {
    // Utiliser setValue sur chaque contrôle individuellement pour forcer la mise à jour
    // Si emitEvent est false, toutes les modifications sont faites sans déclencher d'événements
    // pour permettre de faire toutes les modifications en une seule fois avant de synchroniser avec Firebase
    this.rowsOrder.filter(rKey => this.rowKeys.includes(rKey)).forEach(rKey => {
      const rowControl = questionForm.get(rKey);
      if (!rowControl || rowControl.disabled) {
        return;
      }
      
      const formControlNameControl = rowControl.get(this.formControlName);
      if (!formControlNameControl) return;
      
      this.statementKeys.forEach(sKey => {
        const statementControl = formControlNameControl.get(sKey);
        if (statementControl) {
          // Toujours mettre à jour, même si c'est déjà false
          // Si emitEvent est false, on ne déclenche pas d'événement ici
          statementControl.setValue(false, {emitEvent});
        }
      });
      
      const commentsControlNameControl = rowControl.get(this.commentsControlName);
      if (commentsControlNameControl) {
        this.statementKeys.forEach(sKey => {
          const commentControl = commentsControlNameControl.get(sKey);
          if (commentControl && commentControl.value !== '') {
            commentControl.setValue('', {emitEvent});
          }
        });
      }
    });
  }

  get imageSelection() {
    return this._imageSelection
  }
}
